import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const publicApi = axios.create({ baseURL: API });

export const photoUrl = (u) => (u && u.startsWith("/api") ? `${BACKEND_URL}${u}` : u);
export const errMsg = (e) => e?.response?.data?.detail || e?.message || "Something went wrong";

const REF_KEY = "vr_ref";
const params = new URLSearchParams(window.location.search);
if (params.get("ref")) localStorage.setItem(REF_KEY, params.get("ref").toUpperCase());

const Ctx = createContext(null);

export function AccountProvider({ children }) {
  const { isSignedIn, getToken, isLoaded } = useAuth();
  const [account, setAccount] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const authed = useCallback(async (method, url, data, config = {}) => {
    const token = await getToken();
    const res = await axios({ method, url: `${API}${url}`, data, ...config, headers: { ...(config.headers || {}), Authorization: `Bearer ${token}` } });
    return res.data;
  }, [getToken]);

  const refresh = useCallback(async () => {
    if (!isSignedIn) { setAccount(null); setProfile(null); setLoading(false); return; }
    try {
      const d = await authed("get", "/me");
      setAccount(d.account); setProfile(d.profile);
      const ref = localStorage.getItem(REF_KEY);
      if (ref && !d.account.referred_by && ref !== d.account.ref_code) {
        const r = await authed("post", "/referral/claim", { code: ref }).catch(() => ({}));
        if (r.ok) toast.success("Invite applied! You'll both get ₹10 chat credit after your first plan");
      }
      if (ref) localStorage.removeItem(REF_KEY);
    } finally { setLoading(false); }
  }, [isSignedIn, authed]);

  useEffect(() => { if (isLoaded) refresh(); }, [isLoaded, refresh]);

  const toggleShortlist = useCallback(async (pid) => {
    const on = account?.shortlist?.includes(pid);
    try {
      const r = await authed(on ? "delete" : "post", `/shortlist/${pid}`);
      setAccount((a) => ({ ...a, shortlist: r.shortlist }));
      toast.success(on ? "Removed from shortlist" : "Added to shortlist");
    } catch (e) { toast.error(errMsg(e)); }
  }, [account, authed]);

  const value = useMemo(() => ({ account, setAccount, profile, setProfile, refresh, authed, isSignedIn, toggleShortlist,
    isAdmin: account?.role === "admin", loading: !isLoaded || loading }),
    [account, profile, refresh, authed, isSignedIn, isLoaded, loading, toggleShortlist]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useAccount = () => useContext(Ctx);
